import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { Subscription } from 'rxjs';

import { IbnWizardService } from './ibn-wizard.service';
import { IbnWizardSchritt } from './ibn-wizard-schritte';


type Platzierung =
  | 'oben'
  | 'unten'
  | 'rechts'
  | 'links'
  | 'mitte'
  | 'ueber';


interface Rechteck {
  top: number;
  left: number;
  width: number;
  height: number;
}


/**
 * Coachmark des IBN-Assistenten: Abdunklung, Hervorhebung und Popup.
 *
 * Liegt einmal global in der App. Das Ziel wird laufend nachgemessen, weil
 * es sich durch Scrollen, Aufklappen oder Nachladen verschiebt. Die Seite
 * bleibt vollständig bedienbar – Abdunklung und Hervorhebung lassen jeden
 * Klick durch, nur das Popup selbst nimmt Eingaben an.
 */
@Component({
  selector: 'app-ibn-wizard-overlay',
  templateUrl: './ibn-wizard-overlay.component.html',
  styleUrls: ['./ibn-wizard-overlay.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IbnWizardOverlayComponent implements OnInit, OnDestroy {

  /** Luft zwischen Ziel und Hervorhebung. */
  private static readonly RAHMEN = 6;

  /** Abstand zwischen Hervorhebung und Popup. */
  private static readonly ABSTAND = 14;

  /** Mindestabstand des Popups zum Viewport-Rand. */
  private static readonly RAND = 12;

  /** So lange darf ein Ziel nach einem Seitenwechsel auf sich warten lassen. */
  private static readonly WARTEZEIT_MS = 1500;

  private static readonly TAKT_MS = 150;


  @ViewChild('popup')
  popupRef?: ElementRef<HTMLElement>;

  sichtbar = false;
  schritt: IbnWizardSchritt | null = null;

  nummer = 0;
  anzahl = 0;

  ziel: Rechteck | null = null;

  platzierung: Platzierung = 'mitte';
  popupTop = 0;
  popupLeft = 0;
  pfeil = 0;

  bereit = false;
  kannZurueck = false;
  istLetzter = false;

  private sub?: Subscription;
  private timer: ReturnType<typeof setInterval> | null = null;

  private letzterIndex = -1;
  private letzteUrl = '';
  private seiteSeit = 0;
  private gescrollt = false;

  private readonly aufAenderung = () => this.aktualisieren();


  constructor(
    private wizard: IbnWizardService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) { }


  ngOnInit(): void {

    this.sub = this.wizard.zustand$.subscribe(zustand => {

      if (zustand.aktiv) {
        this.beobachten();
      } else {
        this.beobachtenBeenden();
      }

      this.aktualisieren();
    });
  }


  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.beobachtenBeenden();
  }


  // ========================================================
  // TEMPLATE
  // ========================================================

  get hinweis(): string {

    if (!this.schritt) {
      return '';
    }

    return this.schritt.wartetAufSeitenwechsel || !this.bereit
      ? this.schritt.wartetText ?? ''
      : '';
  }


  get zeigePfeil(): boolean {
    return this.platzierung !== 'mitte' && this.platzierung !== 'ueber';
  }


  get pfeilHorizontal(): boolean {
    return this.platzierung === 'oben' || this.platzierung === 'unten';
  }


  weiter(): void {
    this.wizard.weiter();
  }


  zurueck(): void {
    this.wizard.zurueck();
  }


  beenden(): void {
    this.wizard.beenden();
  }


  // ========================================================
  // BEOBACHTEN
  // ========================================================

  private beobachten(): void {

    if (this.timer !== null) {
      return;
    }

    // Außerhalb der Zone: Scrollen und Nachmessen lösen keine app-weite Prüfung aus.
    this.zone.runOutsideAngular(() => {

      this.timer = setInterval(this.aufAenderung, IbnWizardOverlayComponent.TAKT_MS);

      window.addEventListener('scroll', this.aufAenderung, { capture: true, passive: true });
      window.addEventListener('resize', this.aufAenderung, { passive: true });
    });
  }


  private beobachtenBeenden(): void {

    if (this.timer === null) {
      return;
    }

    clearInterval(this.timer);
    this.timer = null;

    window.removeEventListener('scroll', this.aufAenderung, { capture: true });
    window.removeEventListener('resize', this.aufAenderung);
  }


  private aktualisieren(): void {

    const zustand = this.wizard.zustand;
    const schritt = this.wizard.aktuellerSchritt;

    // Inaktiv oder Seite noch im Wechsel: nichts zeigen.
    if (!schritt || this.wizard.aktuelleSeite() !== schritt.seite) {
      this.ausblenden();
      return;
    }

    const jetzt = Date.now();
    const url = this.wizard.aktuelleUrl();

    if (url !== this.letzteUrl) {
      this.letzteUrl = url;
      this.seiteSeit = jetzt;
    }

    if (zustand.index !== this.letzterIndex) {
      this.letzterIndex = zustand.index;
      this.gescrollt = false;
    }

    const element = this.wizard.findeZiel(schritt.ziele);
    const hatZiele = !!schritt.ziele?.length;
    const seiteLaedt = jetzt - this.seiteSeit < IbnWizardOverlayComponent.WARTEZEIT_MS;

    if (hatZiele && !element) {

      if (seiteLaedt) {
        this.ausblenden();
        return;
      }

      if (schritt.optional) {
        this.zone.run(() => this.wizard.ueberspringen());
        return;
      }
    }

    if (element && !this.gescrollt) {
      this.gescrollt = true;
      this.inSichtRollen(element);
    }

    this.schritt = schritt;
    // Zählung je Weg: bei einer bestehenden Inbetriebnahme entfallen die Importschritte.
    this.nummer = this.wizard.schrittNummer;
    this.anzahl = this.wizard.schrittAnzahl;
    this.istLetzter = this.wizard.istLetzterSchritt;
    this.bereit = this.wizard.istBereit(schritt);
    this.kannZurueck = this.wizard.kannZurueck();
    this.ziel = element
      ? this.mitRahmen(element.getBoundingClientRect())
      : null;

    this.sichtbar = true;

    // Erst rendern, dann das Popup messen und platzieren – beides vor dem nächsten Paint.
    this.cdr.detectChanges();
    this.platzieren();
    this.cdr.detectChanges();
  }


  private ausblenden(): void {

    if (!this.sichtbar) {
      return;
    }

    this.sichtbar = false;
    this.schritt = null;
    this.ziel = null;

    this.cdr.detectChanges();
  }


  // ========================================================
  // POSITION
  // ========================================================

  private inSichtRollen(element: HTMLElement): void {

    const r = element.getBoundingClientRect();
    const hoehe = window.innerHeight;

    if (r.top >= 72 && r.bottom <= hoehe - 24) {
      return;
    }

    const ruhig =
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    element.scrollIntoView({
      behavior: ruhig ? 'auto' : 'smooth',
      block: r.height > hoehe * 0.5 ? 'start' : 'center'
    });
  }


  private mitRahmen(r: DOMRect): Rechteck {

    const rahmen = IbnWizardOverlayComponent.RAHMEN;

    return {
      top: r.top - rahmen,
      left: r.left - rahmen,
      width: r.width + 2 * rahmen,
      height: r.height + 2 * rahmen
    };
  }


  /**
   * Bevorzugt oben, dann unten, rechts, links. Passt nichts (sehr großes
   * Ziel, schmales Display), liegt das Popup am Viewport-Rand über dem Ziel.
   */
  private platzieren(): void {

    const popup = this.popupRef?.nativeElement;

    if (!popup) {
      return;
    }

    const RAND = IbnWizardOverlayComponent.RAND;
    const ABSTAND = IbnWizardOverlayComponent.ABSTAND;

    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    const pw = popup.offsetWidth;
    const ph = popup.offsetHeight;

    const z = this.ziel;

    let top: number;
    let left: number;
    let platzierung: Platzierung;

    const mitteX = z ? z.left + z.width / 2 : vw / 2;
    const mitteY = z ? z.top + z.height / 2 : vh / 2;

    if (!z) {

      platzierung = 'mitte';
      top = (vh - ph) / 2;
      left = (vw - pw) / 2;

    } else if (z.top >= ph + ABSTAND + RAND) {

      platzierung = 'oben';
      top = z.top - ABSTAND - ph;
      left = mitteX - pw / 2;

    } else if (vh - (z.top + z.height) >= ph + ABSTAND + RAND) {

      platzierung = 'unten';
      top = z.top + z.height + ABSTAND;
      left = mitteX - pw / 2;

    } else if (vw - (z.left + z.width) >= pw + ABSTAND + RAND) {

      platzierung = 'rechts';
      left = z.left + z.width + ABSTAND;
      top = mitteY - ph / 2;

    } else if (z.left >= pw + ABSTAND + RAND) {

      platzierung = 'links';
      left = z.left - ABSTAND - pw;
      top = mitteY - ph / 2;

    } else {

      platzierung = 'ueber';
      left = (vw - pw) / 2;
      top = mitteY > vh * 0.6 ? RAND : vh - ph - RAND;
    }

    this.platzierung = platzierung;
    this.popupLeft = this.begrenze(left, RAND, vw - pw - RAND);
    this.popupTop = this.begrenze(top, RAND, vh - ph - RAND);

    this.pfeil = this.pfeilHorizontal
      ? this.begrenze(mitteX - this.popupLeft, 18, pw - 18)
      : this.begrenze(mitteY - this.popupTop, 18, ph - 18);
  }


  private begrenze(wert: number, min: number, max: number): number {
    return Math.max(min, Math.min(wert, Math.max(min, max)));
  }
}
